import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';

import { weakKey } from '@hotjar/webapp/lib/weakKey';
import type { Sentiment } from '@hotjar/webapp/domains/activation/feedback-widgets/types';
import { SentimentIcon } from './SentimentIcon';

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: ${tokens.spacer.x1};
`;

const Percentage = styled.a`
  color: ${tokens.color.text_light};
  padding: ${tokens.spacer.x0};
  border-radius: ${tokens.radius.m};
  display: flex;
  gap: ${tokens.spacer.x0};
  ${tokens.text('subtitle3')};

  &:hover {
    color: ${tokens.color.text_light};
    background: ${tokens.color.neutral_100};
    text-decoration: none;
  }
`;

function valueToRoundedPercent(value: number, total: number) {
  // In fact total can never be 0 due to checks higher up, but let's not be too trusting.
  return total === 0 ? 0 : Math.round((value / total) * 1000) / 10;
}

interface SentimentProps {
  siteId: number;
  widgetId: number;
  sentiment: Sentiment;
  responseTotal: number;
  onSentimentClick: (score: number) => void;
}

export function SentimentSpread({
  siteId,
  widgetId,
  responseTotal,
  sentiment,
  onSentimentClick,
}: SentimentProps): JSX.Element {
  const sentimentValues = Object.values(sentiment);
  return (
    <Container>
      {sentimentValues.map((value, i) => (
        <Percentage
          data-testid={`sentiment-${i}`}
          onClick={() => onSentimentClick(i + 1)}
          key={weakKey({ value })}
          href={`/sites/${siteId}/feedback/responses/${widgetId}?rating=${i + 1}`}
        >
          <SentimentIcon average={i + 1} size={16} /> {valueToRoundedPercent(value, responseTotal)}%
        </Percentage>
      ))}
    </Container>
  );
}
